import {
  Box,
  Stack,
  type SxProps,
  type Theme,
  Typography,
  useTheme,
} from "@mui/material";
import type { ReactNode } from "react";

export type InfoChipTone =
  | "neutral"
  | "success"
  | "warning"
  | "error"
  | "orange"
  | "blue"
  | "purple"
  | "purple-outline"
  | "violet"
  // 1px border in the ramp's 100 step, dark text — the Email Exclusion
  // Rules condition-type tags (Domain / Email address / Keyword).
  // orange-bordered sits on amber[50] (UX audit, Oct 2026).
  | "orange-bordered"
  | "green-bordered"
  | "magenta-bordered"
  | "violet-outline"
  | "magenta"
  | "indigo";

interface InfoChipProps {
  label: string;
  /**
   * Optional leading element (MUI icon, a dot, etc.). Pass `dot` to render a
   * status dot colored to match the tone — useful for "Active" / "Inactive".
   */
  icon?: ReactNode | "dot";
  tone?: InfoChipTone;
  /** Optional trailing element after the label (e.g. a dropdown chevron). */
  endIcon?: ReactNode;
  /** Per-instance style overrides (width, padding, etc.) */
  sx?: SxProps<Theme>;
  /** Diameter of the dot indicator when `icon="dot"`. Default 8px. */
  dotSize?: number | string;
}

interface ToneStyles {
  bg: string | undefined;
  fg: string | undefined;
  border?: string | undefined;
  dot?: string | undefined;
  /** Label colour override. Defaults to `fg`. */
  label?: string | undefined;
  /** Label weight override. Defaults to the variant's own weight. */
  labelWeight?: number;
}

const useToneStyles = (tone: InfoChipTone): ToneStyles => {
  const theme = useTheme();
  switch (tone) {
    case "success":
      return {
        bg: theme.palette.primitive.system.green[50],
        fg: theme.palette.primitive.system.green[600],
        dot: theme.palette.success.main,
      };
    case "warning":
      // TODO: replace hard-coded hex with `primitive.system.yellow[800]` once
      // the theme token (#F3C423) is realigned with Figma's Yellow-800 (#937300).
      return {
        bg: theme.palette["warning-minor"].tint,
        fg: theme.palette.lightBg.high,
        dot: "#937300",
      };
    case "error":
      return {
        bg: theme.palette.error.tint,
        fg: theme.palette.lightBg.high,
        dot: theme.palette.error.main,
      };
    case "orange":
      return {
        bg: theme.palette.primitive.system.orange[50],
        fg: theme.palette.lightBg.high,
        dot: theme.palette.primitive.system.orange[800],
      };
    case "blue":
      return {
        bg: theme.palette.primitive.system.blue[50],
        fg: theme.palette.primitive.system.blue[800],
      };
    case "magenta":
      return {
        bg: theme.palette.primitive.system.magenta[50],
        fg: theme.palette.primitive.system.magenta[800],
      };
    case "indigo":
      return {
        bg: theme.palette.primitive.system.indigo[50],
        fg: theme.palette.primitive.system.indigo[800],
      };
    case "purple":
      return {
        bg: theme.palette.primitive.brand.purple[50],
        fg: theme.palette.primitive.brand.purple[800],
      };
    case "purple-outline":
      return {
        bg: theme.palette.primitive.brand.purple[50],
        fg: theme.palette.primitive.brand.purple[600],
      };
    case "violet":
      // TODO: replace hard-coded hex with `primitive.system.violet[50]` once
      // the design-system token in `theme.ts` is synced with Figma.
      return {
        bg: "#EDE9FE",
        fg: theme.palette.primitive.system.violet[800],
      };
    case "orange-bordered":
      return {
        bg: theme.palette.primitive.system.amber[50],
        fg: theme.palette.lightBg.high,
        border: theme.palette.primitive.system.orange[100],
      };
    case "green-bordered":
      return {
        bg: theme.palette.primitive.system.green[50],
        fg: theme.palette.lightBg.high,
        border: theme.palette.primitive.system.green[100],
      };
    case "magenta-bordered":
      return {
        bg: theme.palette.primitive.system.magenta[50],
        fg: theme.palette.lightBg.high,
        border: theme.palette.primitive.system.magenta[100],
      };
    case "violet-outline":
      // "Primary" marker (boards, recurring invoices): outlined, coloured label.
      return {
        bg: theme.palette.primitive.system.violet[100],
        fg: theme.palette.primitive.system.violet[700],
        border: theme.palette.primitive.system.violet[700],
        label: theme.palette.primitive.system.violet[700],
        labelWeight: 600,
      };
    default:
      return {
        bg: theme.palette.neutral["010"],
        fg: theme.palette.lightBg.high,
        dot: theme.palette.lightBg.low,
      };
  }
};

export const InfoChip = ({
  label,
  icon,
  tone = "neutral",
  endIcon,
  sx,
  dotSize = "8px",
}: InfoChipProps) => {
  const styles = useToneStyles(tone);

  const renderedIcon =
    icon === "dot" ? (
      <Box
        sx={{
          width: dotSize,
          height: dotSize,
          borderRadius: "50%",
          backgroundColor: styles.dot ?? styles.fg,
          flexShrink: 0,
        }}
      />
    ) : (
      icon
    );

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap="4px"
      sx={[
        {
          height: "24px",
          padding: "4px 8px",
          borderRadius: "4px",
          backgroundColor: styles.bg,
          color: styles.fg,
          border: styles.border ? `1px solid ${styles.border}` : "none",
          flexShrink: 0,
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {renderedIcon}
      <Typography
        variant="caption-xs"
        sx={{
          color: styles.label ?? styles.fg,
          fontWeight: styles.labelWeight,
        }}
      >
        {label}
      </Typography>
      {endIcon}
    </Stack>
  );
};
